import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import FilterableShiftTable from '../components/FilterableShiftTable';
import PortalTimings from './PortalTimings';
import PortalLogs from './PortalLogs';
import PortalActiveRuns from './PortalActiveRuns';
import PortalRequests from './PortalRequests';
import { FleetTab } from '../admin/AdminDashboard';

// Portal-scoped data adapter for the shared Fleet component (client sees only their
// assigned machines + their own profiles; admin-only UI is hidden via isAdmin={false}).
const PORTAL_FLEET_API = {
  getSlots: api.getPortalSlots, getBotProfiles: api.getPortalBotProfiles, createBotProfile: api.createPortalBotProfile,
  updateBotProfile: api.updatePortalBotProfile, deleteBotProfile: api.deletePortalBotProfile, revealBotProfile: api.revealPortalBotProfile,
  bulkBotProfiles: api.bulkPortalBotProfiles, createChromeProfile: api.createPortalChromeProfile, deleteChromeProfile: api.deletePortalChromeProfile,
  startRun: api.startPortalRun, stopRun: api.stopPortalRun, getRuns: api.getPortalRuns, getLiveLog: api.getPortalLiveLog
};

const fmt = (n) => `$${(n || 0).toFixed(2)}`;
const fmtDate = (d) => { if (!d) return ''; const [y,m,day] = d.split('-'); return `${parseInt(m)}/${parseInt(day)}/${y}`; };
const TWELVE_WEEKS = 84 * 24 * 60 * 60 * 1000;

function filterShifts(shifts) {
  const cutoff = Date.now() - TWELVE_WEEKS;
  return shifts.filter(s => {
    if (!s.paid) return true;
    if (s.needsIdentification) return true;
    if (s.datePaid) return new Date(s.datePaid).getTime() >= cutoff;
    return true;
  });
}

// /portal/me and /portal/managed both return amountDue alongside the total balance,
// computed by backend utils/balanceCalc — the same math the reminder emails quote.
// Monthly accounts carry a renewal that bill.balance never shows, so never fall back
// to a balance here.
const dueNowForAccount = (a) => a.amountDue || 0;

// "in 3 days" / "tomorrow" / "today" — mirrors inDaysPhrase in backend balanceCalc.
function inDaysPhrase(days) {
  if (days === null || days === undefined) return '';
  if (days < 0) return '';
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  return `in ${days} days`;
}

// A monthly account's renewal, in one of three states:
//   'lapsed'   — the term ran out; the amount is overdue
//   'due'      — inside the backend's 10-day due window: owed now, service still on
//   'upcoming' — outside the window: a heads-up only, so they can pay ahead
// null for per-shift accounts, or when no rate is set.
function renewalFor(a) {
  if (a.billingType !== 'perMonth') return null;
  const state = a.renewalLapsed ? 'lapsed' : a.renewalDue ? 'due' : 'upcoming';
  // Inside the window (and once lapsed) the charge already sits in amountDue;
  // outside it, nextCharge carries the quote.
  const amount = state === 'upcoming' ? a.nextCharge : a.amountDue;
  if (!amount) return null;
  return { amount, date: a.nextChargeDate, state, daysUntil: a.daysUntilRenewal };
}

export default function ClientPortal() {
  const [data, setData] = useState(null);
  const [managedAccounts, setManagedAccounts] = useState([]);
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [tab, setTab] = useState('Billing');
  const commissions = data?.commission || [];
  const commissionShifts = data?.commissionShifts || [];
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const clientName = localStorage.getItem('clientName');

  useEffect(() => {
    api.getMe().then(setData).catch(err => {
      if (err.message.includes('token') || err.message.includes('Invalid')) {
        localStorage.clear(); navigate('/portal/login');
      } else setError(err.message);
    });
    api.getManagedAccounts().then(setManagedAccounts).catch(() => {});
  }, [navigate]);

  function logout() { localStorage.clear(); navigate('/portal/login'); }

  if (!data) return <div style={{ padding: 32 }}>{error || 'Loading...'}</div>;

  const { summary, bills } = data;

  // Kimji commission summary — current month net
  const currentPeriod = new Date().toISOString().slice(0, 7);
  const thisMonthCommission = commissions.find(c => c.period === currentPeriod);
  const commissionEarned = thisMonthCommission?.totalEarned || 0;
  const monthlyFee = data?.monthlyRate || bills[0]?.netAmount || 0;
  const netOwed = monthlyFee - commissionEarned; // negative = admin owes Kimji

  return (
    <>
      <header className="app-header">
        <h1>Shift Billing — {clientName}</h1>
        <button className="btn btn-secondary btn-sm" onClick={logout}>Sign Out</button>
      </header>
      <div className="page">
        {error && <div className="alert alert-error">{error}</div>}

        <div className="tabs">
          {['Billing', ...(data.requestsEnabled ? ['Request'] : []), ...(data.fleetEnabled ? ['Fleet'] : []), 'Timings', 'Logs'].map(t => (
            <button key={t} className={`tab${tab === t ? ' active' : ''}`} onClick={() => setTab(t)}>{t}</button>
          ))}
        </div>

        {tab === 'Fleet' && data.fleetEnabled ? (
          <FleetTab A={PORTAL_FLEET_API} isAdmin={false} />
        ) : tab === 'Request' && data.requestsEnabled ? (
          <PortalRequests />
        ) : tab === 'Timings' ? (
          <PortalTimings />
        ) : tab === 'Logs' ? (
          <PortalLogs />
        ) : (
        <>
        <div className="rotate-hint">
          📱 Rotate to landscape for the best view
        </div>

        <PortalActiveRuns />

        {/* Balance Summary */}
        {commissions.length > 0 ? (
          // Kimji — show monthly commission vs fee
          <div className="summary-grid">
            <div className="summary-card paid">
              <div className="label">Commission ({currentPeriod})</div>
              <div className="value">{fmt(commissionEarned)}</div>
            </div>
            <div className="summary-card outstanding">
              <div className="label">Monthly Fee</div>
              <div className="value">{fmt(monthlyFee)}</div>
            </div>
            <div className="summary-card" style={{ border: '2px solid #1a1a2e' }}>
              <div className="label">{netOwed < 0 ? 'Owed to You' : netOwed === 0 ? 'Even' : 'You Owe'}</div>
              <div className="value" style={{ color: netOwed < 0 ? '#38a169' : netOwed === 0 ? '#718096' : '#e53e3e' }}>
                {fmt(Math.abs(netOwed))}
              </div>
            </div>
          </div>
        ) : (
          // Everyone else — if manager, show group total
          <div className="summary-grid">
            {managedAccounts.length > 0 ? (() => {
              // The manager's own account is just another line in the group, so it goes
              // through the same shape as the managed ones. summary.outstanding already
              // reflects a monthly renewal (see /portal/me), so no special-casing here.
              const self = {
                clientName, isSelf: true,
                billingType: data.billingType,
                balance: summary.outstanding,
                amountDue: summary.balanceDue,
                nextCharge: summary.nextCharge,
                nextChargeDate: summary.nextChargeDate,
                renewalDue: summary.renewalDue,
                renewalLapsed: summary.renewalLapsed,
                daysUntilRenewal: summary.daysUntilRenewal
              };
              const accounts = [self, ...managedAccounts];
              const groupTotal = accounts.reduce((s, a) => s + (a.balance || 0), 0);
              const groupDue = accounts.reduce((s, a) => s + dueNowForAccount(a), 0);
              return (
                <>
                  <div className="summary-card" style={{ border: '1px solid #e2e8f0' }}>
                    <div className="label">Group Total Balance</div>
                    <div className="value" style={{ color: '#4a5568' }}>{fmt(groupTotal)}</div>
                  </div>
                  <div className="summary-card outstanding">
                    <div className="label">Group Balance Due</div>
                    <div className="value" style={{ color: groupDue > 0 ? '#e53e3e' : '#38a169' }}>{fmt(groupDue)}</div>
                  </div>
                  {accounts.map(a => {
                    const due = dueNowForAccount(a);
                    const renewal = renewalFor(a);
                    return (
                      <div className="summary-card" key={a.clientName}>
                        <div className="label">
                          {a.clientName}{a.isSelf ? ' (you)' : ''}
                        </div>
                        {/* Total balance stays neutral; only the part that has actually
                            come due is called out in red. */}
                        <div className="value" style={{ color: '#4a5568' }}>{fmt(a.balance || 0)}</div>
                        <div style={{ fontSize: 12, marginTop: 4, fontWeight: 600, color: due > 0 ? '#e53e3e' : '#38a169' }}>
                          {due > 0 ? `Due now: ${fmt(due)}` : 'Nothing due'}
                        </div>
                        {/* Monthly subscribers owe a renewal the bill balance never shows.
                            Inside the due window it is already counted in "Due now" above,
                            so this line explains the charge rather than repeating it. */}
                        {renewal && renewal.state === 'due' && (
                          <div style={{ fontSize: 11, marginTop: 2, color: '#e53e3e' }}>
                            Next month{renewal.date ? ` — renews ${fmtDate(renewal.date)}` : ''}
                            {inDaysPhrase(renewal.daysUntil) ? `, ${inDaysPhrase(renewal.daysUntil)}` : ''}
                          </div>
                        )}
                        {renewal && renewal.state === 'upcoming' && (
                          <div style={{ fontSize: 11, marginTop: 2, color: '#718096' }}>
                            Next month: {fmt(renewal.amount)}
                            {renewal.date ? ` on ${fmtDate(renewal.date)}` : ''}
                          </div>
                        )}
                        {renewal && renewal.state === 'lapsed' && (
                          <div style={{ fontSize: 11, marginTop: 2, color: '#e53e3e' }}>
                            Subscription expired{renewal.date ? ` ${fmtDate(renewal.date)}` : ''}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              );
            })() : data.billingType === 'perShift' ? (
              <>
                <div className="summary-card outstanding">
                  <div className="label">Balance Due Now</div>
                  <div className="value">{fmt(summary.balanceDue)}</div>
                </div>
                <div className="summary-card" style={{ border: '1px solid #e2e8f0' }}>
                  <div className="label">Total Balance</div>
                  <div className="value" style={{ color: '#4a5568' }}>{fmt(summary.outstanding)}</div>
                </div>
              </>
            ) : (
              // Solo monthly subscriber — what is owed now, plus the upcoming renewal
              // so they can pay ahead instead of only hearing about it once it lapses.
              <>
                <div className="summary-card outstanding">
                  <div className="label">Balance Due</div>
                  <div className="value" style={{ color: summary.outstanding > 0 ? '#e53e3e' : '#38a169' }}>
                    {fmt(summary.outstanding)}
                  </div>
                  {summary.renewalLapsed && summary.nextChargeDate && (
                    <div style={{ fontSize: 11, marginTop: 2, color: '#e53e3e' }}>
                      Subscription expired {fmtDate(summary.nextChargeDate)}
                    </div>
                  )}
                  {/* Inside the due window: the next month is already in the figure
                      above, so name the renewal date rather than repeating the amount. */}
                  {!summary.renewalLapsed && summary.renewalDue && summary.nextChargeDate && (
                    <div style={{ fontSize: 11, marginTop: 2, color: '#e53e3e' }}>
                      Next month — renews {fmtDate(summary.nextChargeDate)}
                      {inDaysPhrase(summary.daysUntilRenewal) ? `, ${inDaysPhrase(summary.daysUntilRenewal)}` : ''}
                    </div>
                  )}
                </div>
                {summary.nextCharge > 0 && (
                  <div className="summary-card" style={{ border: '1px solid #e2e8f0' }}>
                    <div className="label">Next Month</div>
                    <div className="value" style={{ color: '#4a5568' }}>{fmt(summary.nextCharge)}</div>
                    {summary.nextChargeDate && (
                      <div style={{ fontSize: 11, marginTop: 2, color: '#718096' }}>
                        Due {fmtDate(summary.nextChargeDate)}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Commission section — Kimji only */}
        {commissions.length > 0 && (
          <div className="card" style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <strong style={{ fontSize: 15 }}>Commission Earned</strong>
              <button className="toggle-btn" onClick={() => setShowAllHistory(p => !p)} style={{ fontSize: 12 }}>
                {showAllHistory ? 'Show Recent' : 'Show All'}
              </button>
            </div>
            {(showAllHistory ? commissions : commissions.slice(0, 3)).map(c => {
              const net = c.totalEarned - monthlyFee;
              return (
                <div key={c._id} style={{ marginBottom: 10, paddingBottom: 10, borderBottom: '1px solid #f0f2f5' }}>
                  {/* Monthly net summary */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <strong style={{ fontSize: 13 }}>{c.period}</strong>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 12 }}>
                      <span style={{ color: '#718096' }}>Commission: <strong style={{ color: '#38a169' }}>{fmt(c.totalEarned)}</strong></span>
                      <span style={{ color: '#718096' }}>Fee: <strong style={{ color: '#e53e3e' }}>−{fmt(monthlyFee)}</strong></span>
                      <span style={{ fontWeight: 700, color: net >= 0 ? '#38a169' : '#e53e3e' }}>
                        {net >= 0 ? `Owed to you: ${fmt(net)}` : `You owe: ${fmt(Math.abs(net))}`}
                      </span>
                      {net !== 0 && (
                        c.settled
                          ? <span className="badge badge-paid">
                              {net > 0 ? `Paid ${fmtDate(c.settledDate)}` : `Received ${fmtDate(c.settledDate)}`}
                            </span>
                          : <span className="badge badge-unpaid">Pending</span>
                      )}
                    </div>
                  </div>
                  {/* Per-person breakdown */}
                  <table style={{ width: '100%' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', fontWeight: 600, color: '#718096' }}>From</th>
                        <th style={{ textAlign: 'left', fontWeight: 600, color: '#718096' }}>Type</th>
                        <th style={{ textAlign: 'right', fontWeight: 600, color: '#718096' }}>Gross Paid</th>
                        <th style={{ textAlign: 'right', fontWeight: 600, color: '#718096' }}>Commission</th>
                        <th style={{ textAlign: 'right', fontWeight: 600, color: '#718096' }}>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {c.entries.map((e, i) => (
                        <tr key={i}>
                          <td>{e.fromClient}</td>
                          <td style={{ color: '#718096' }}>{e.billingType === 'perShift' ? 'Per Shift (25%)' : 'Monthly ($300)'}</td>
                          <td style={{ textAlign: 'right' }}>{fmt(e.grossPaid)}</td>
                          <td style={{ textAlign: 'right', color: '#38a169', fontWeight: 600 }}>{fmt(e.commissionAmount)}</td>
                          <td style={{ textAlign: 'right', color: '#718096' }}>{fmtDate(e.paidDate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        )}

        {/* Commission shifts — one card per generator client */}
        {commissionShifts.length > 0 && (
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8, color: '#4a5568' }}>Commission Shifts</div>
            {commissionShifts.map(g => {
              const activeShifts = g.shifts.filter(s => !s.canceled);
              const gross = activeShifts.reduce((s, sh) => s + (sh.rate || 0), 0);
              const paidShifts = activeShifts.filter(s => s.paid);
              const grossPaid = paidShifts.reduce((s, sh) => s + (sh.rate || 0), 0);
              const commission = g.billingType === 'perShift'
                ? Math.round(grossPaid * 0.25 * 100) / 100
                : paidShifts.length > 0 ? 300 : 0;
              const pendingShifts = activeShifts.filter(s => !s.paid);
              const grossPending = pendingShifts.reduce((s, sh) => s + (sh.rate || 0), 0);
              const commissionPending = g.billingType === 'perShift'
                ? Math.round(grossPending * 0.25 * 100) / 100
                : pendingShifts.length > 0 ? 300 : 0;

              return (
                <div key={g.clientName} className="card" style={{ marginBottom: 6 }}>
                  {/* Header + totals */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
                    <strong style={{ fontSize: 14 }}>{g.clientName}</strong>
                    <div style={{ display: 'flex', gap: 16, fontSize: 12, flexWrap: 'wrap' }}>
                      <span style={{ color: '#718096' }}>
                        Total billed: <strong style={{ color: '#1a1a2e' }}>{fmt(gross)}</strong>
                      </span>
                      <span style={{ color: '#718096' }}>
                        Paid: <strong style={{ color: '#38a169' }}>{fmt(grossPaid)}</strong>
                      </span>
                      {grossPending > 0 && (
                        <span style={{ color: '#718096' }}>
                          Pending: <strong style={{ color: '#e53e3e' }}>{fmt(grossPending)}</strong>
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Commission calculation */}
                  <div style={{ background: '#f7f8fa', borderRadius: 6, padding: '6px 10px', marginBottom: 8, fontSize: 12 }}>
                    <span style={{ color: '#718096' }}>Commission earned: </span>
                    <strong style={{ color: '#38a169' }}>{fmt(commission)}</strong>
                    <span style={{ color: '#aaa', marginLeft: 6 }}>
                      ({g.billingType === 'perShift' ? `25% of ${fmt(grossPaid)} paid` : '$300 flat when paid'})
                    </span>
                    {commissionPending > 0 && (
                      <span style={{ color: '#aaa', marginLeft: 10 }}>
                        · Pending: <strong style={{ color: '#d69e2e' }}>{fmt(commissionPending)}</strong> when paid
                      </span>
                    )}
                  </div>

                  <FilterableShiftTable
                    shifts={g.shifts}
                    billingType={g.billingType}
                    clientView
                    commissionRate={g.billingType === 'perShift' ? 0.25 : null}
                    onClientMarkPaid={async (shiftId, clientMarkedPaid) => {
                      await api.commissionShiftMarkPaid(g.clientName, shiftId, clientMarkedPaid);
                      const updated = await api.getMe();
                      setData(updated);
                    }}
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* Managed accounts — Gil only */}
        {managedAccounts.length > 0 && (
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8, color: '#4a5568' }}>Managed Accounts</div>
            {managedAccounts.map(mc => {
              return (
                <div key={mc.clientName} className="card" style={{ marginBottom: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
                    <strong style={{ fontSize: 14 }}>{mc.clientName}</strong>
                    <div style={{ display: 'flex', gap: 16, fontSize: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                      <span style={{ color: '#718096' }}>
                        {mc.billingType === 'perMonth' ? `Monthly${mc.monthlyRate ? ` · ${fmt(mc.monthlyRate)}/mo` : ''}` : 'Per Shift'}
                      </span>
                      <span className={`badge badge-${mc.status || 'unpaid'}`}>{mc.status || 'unpaid'}</span>
                      <span style={{ fontWeight: 700, color: (mc.balance || 0) > 0 ? '#e53e3e' : '#38a169' }}>
                        Balance: {fmt(mc.balance || 0)}
                      </span>
                    </div>
                  </div>
                  {mc.billingType === 'perMonth' && (
                    <p style={{ marginBottom: 8, color: '#718096', fontSize: 13 }}>
                      Monthly flat rate: {fmt(mc.netAmount)}
                    </p>
                  )}
                  <FilterableShiftTable
                    shifts={mc.shifts || []}
                    billingType={mc.billingType}
                    clientView
                    onClientMarkPaid={mc.billingType === 'perShift' ? async (shiftId, clientMarkedPaid) => {
                      await api.managedShiftMarkPaid(mc.clientName, shiftId, clientMarkedPaid);
                      const updated = await api.getManagedAccounts();
                      setManagedAccounts(updated);
                    } : undefined}
                  />
                </div>
              );
            })}
          </div>
        )}


        {bills.map(bill => (
          <div key={bill._id} className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div className="card-title" style={{ marginBottom: 0 }}>
                {(commissions.length > 0 || managedAccounts.length > 0) && (
                  <span style={{ marginRight: 8 }}>{clientName}</span>
                )}
                <span style={{ fontWeight: 400, fontSize: 13, color: '#718096' }}>Period: {bill.period}</span>
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <span className={`badge badge-${bill.status}`}>{bill.status}</span>
                <span style={{ fontSize: 13 }}>Balance: <strong>{fmt(bill.balance)}</strong></span>
              </div>
            </div>

            {bill.billingType === 'perMonth' && (
              <p style={{ marginBottom: 12, color: '#718096', fontSize: 13 }}>
                Monthly flat rate: {fmt(bill.netAmount)}
              </p>
            )}

            <FilterableShiftTable
              shifts={filterShifts(bill.shifts)}
              billingType={bill.billingType}
              clientView
              onNoteSave={async (shiftId, clientNotes) => {
                await api.updateClientNotes(shiftId, clientNotes);
              }}
              onClientMarkPaid={async (shiftId, clientMarkedPaid) => {
                await api.clientMarkPaid(shiftId, clientMarkedPaid);
                const updated = await api.getMe();
                setData(updated);
              }}
            />
          </div>
        ))}
        </>
        )}
      </div>
    </>
  );
}
